/**
 * Application Bootstrap
 * Initialization and setup logic for the Audelio application
 */

import appState from '../core/app-state.js';
import logger from '../core/logger.js';
import { logEvent } from '../core/event-logger.js';
import { EVENT_TYPES } from '../core/event-types.js';
import router from '../core/router.js';
import { appFlags } from '../config/feature-flags.js';
import * as ProgressService from '../services/progress-service.js';
import { authService } from '../services/auth-service.js';
import { i18nService, loadUIStrings } from '../services/i18n-service.js';
import { loadPreferences, renderSettingsModal } from '../ui/settings-modal.js';
import * as TranscriptModule from '../modules/player/transcript.js';
import { generateDailySet } from '../modules/daily-set/daily-set-generator.js';
import { showOnboardingModal } from '../modules/home/onboarding.js';
import { updateFooter, showToast } from '../ui/ui-helpers.js';
import { setupMobileMenuHandlers } from '../ui/event-handlers.js';
import type { Clip } from '../types/clip.js';

/**
 * Helper function to get query parameter
 */
function getQueryParam(name: string): string | null {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get(name);
}

/**
 * Helper function to normalize clip metadata
 * Coerces numeric string metadata to integers and handles optional fields
 */
export function normalizeClipMetadata(clip: any): Clip {
  const normalized = { ...clip };
  
  // Coerce storyId, storyPart, and lessonPosition to integers if they are numeric strings
  if (typeof normalized.storyId === 'string' && /^\d+$/.test(normalized.storyId)) {
    normalized.storyId = parseInt(normalized.storyId, 10);
  }
  if (typeof normalized.storyPart === 'string' && /^\d+$/.test(normalized.storyPart)) {
    normalized.storyPart = parseInt(normalized.storyPart, 10);
  }
  if (typeof normalized.lessonPosition === 'string' && /^\d+$/.test(normalized.lessonPosition)) {
    normalized.lessonPosition = parseInt(normalized.lessonPosition, 10);
  }
  
  // Normalize transcript field: new schema uses 'sentences', legacy uses 'transcript'
  if (normalized.sentences && !normalized.transcript) {
    normalized.transcript = normalized.sentences;
  } else if (normalized.transcript && !normalized.sentences) {
    normalized.sentences = normalized.transcript;
  }
  
  return normalized as Clip;
}

/**
 * Touch detection for mobile-friendly UI
 */
export function applyTouchModeIfNeeded(): void {
  if (document.body.classList.contains('is-touch')) return;
  
  // Comprehensive touch detection for iOS Safari
  const isTouchDevice = (
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0 ||
    (navigator as any).msMaxTouchPoints > 0 ||
    /iPhone|iPad|iPod|Android|webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
    (window.matchMedia && window.matchMedia('(pointer: coarse)').matches)
  );
  
  logger.debug('Touch Detection', {
    ontouchstart: 'ontouchstart' in window,
    maxTouchPoints: navigator.maxTouchPoints,
    userAgent: navigator.userAgent.includes('iPhone') || navigator.userAgent.includes('iPad'),
    matchMedia: window.matchMedia && window.matchMedia('(pointer: coarse)').matches,
    result: isTouchDevice
  });
  
  if (isTouchDevice) {
    document.body.classList.add('is-touch');
    const eventType = EVENT_TYPES['TRANSCRIPT_REPLAY_AFFORDANCE_MODE'];
    if (eventType) {
      logEvent(eventType, { mode: 'always-visible-touch' });
    }
  }
}

/**
 * Load clip data from JSON file
 */
export async function loadClips(
  switchMode: (mode: string) => void,
  setCurrentClip: (idx: number) => void,
  updateProgressUI: () => void
): Promise<void> {
  // Determine which pack to load (override or default)
  let packPath = 'data/clips.json';
  let packOverride: string | null = null;
  
  // Check query parameter first
  const queryPack = getQueryParam('pack');
  if (queryPack) {
    packOverride = queryPack;
  } else {
    // Check localStorage
    try {
      const storedPack = localStorage.getItem('listenUpPackOverride');
      if (storedPack) {
        packOverride = storedPack;
      }
    } catch (e) {
      // localStorage might not be available
    }
  }
  
  if (packOverride) {
    packPath = packOverride;
    logger.info('Pack override detected', { packPath });
  } else {
    logger.info('Loading clips from data/clips.json');
  }
  
  try {
    const res = await fetch(packPath);
    if (!res.ok) {
      throw new Error(`Failed to load clips: ${res.status} ${res.statusText}`);
    }
    const data = await res.json();
    
    // Normalize pack data: accept both array and pack object formats
    let clipsData: any[];
    if (Array.isArray(data)) {
      clipsData = data;
    } else if (data && Array.isArray(data.appState?.clips)) {
      clipsData = data.appState.clips;
      if (packOverride) {
        logger.info('Loaded pack', { title: data.title || data.id || 'unnamed' });
      }
    } else {
      throw new Error('Invalid pack format: expected array or object with clips property');
    }
    
    logger.info('Clips loaded successfully', { count: clipsData.length });
    // Normalize metadata for all clips
    appState.clips = clipsData.map(normalizeClipMetadata);
    
    // Validate that all clips have required difficulty field
    appState.clips.forEach(clip => {
      if (!clip.difficulty) {
        logger.warn('Clip missing difficulty field', { clipId: clip.id });
      }
    });
    
    // Generate daily set after clips are loaded
    generateDailySet();
    
    // Initialize library filter from preferences
    if (appState.preferences['filterLevel'] && appState.preferences['filterLevel'] !== 'all') {
      appState.libraryFilters.difficulty = appState.preferences['filterLevel'] as string;
    }
    
    // Start in home mode if there are clips
    if (appState.clips.length > 0) {
      logger.info('Starting in home mode');
      switchMode('home');
    } else {
      logger.warn('No clips found, falling back to exercise mode');
      appState.currentClipIdx = 0;
      switchMode('exercise');
      setCurrentClip(appState.currentClipIdx);
      updateProgressUI();
    }
  } catch (err: any) {
    // If override failed, try fallback to default
    if (packOverride && packPath !== 'data/clips.json') {
      logger.warn('Failed to load pack override', { packPath, error: err.message });
      logger.warn('Falling back to default clips.json');
      
      // Retry with default path
      try {
        const res = await fetch('data/clips.json');
        if (!res.ok) {
          throw new Error(`Failed to load clips: ${res.status} ${res.statusText}`);
        }
        const data = await res.json();
        
        // Normalize pack data for fallback
        let clipsData: any[];
        if (Array.isArray(data)) {
          clipsData = data;
        } else if (data && Array.isArray(data.appState?.clips)) {
          clipsData = data.appState.clips;
        } else {
          throw new Error('Invalid pack format: expected array or object with clips property');
        }
        
        logger.info('Clips loaded successfully (fallback)', { count: clipsData.length });
        appState.clips = clipsData.map(normalizeClipMetadata);
        
        appState.clips.forEach(clip => {
          if (!clip.difficulty) {
            logger.warn('Clip missing difficulty field', { clipId: clip.id });
          }
        });
        
        generateDailySet();
        
        if (appState.preferences['filterLevel'] && appState.preferences['filterLevel'] !== 'all') {
          appState.libraryFilters.difficulty = appState.preferences['filterLevel'] as string;
        }
        
        if (appState.clips.length > 0) {
          logger.info('Starting in home mode');
          switchMode('home');
        } else {
          logger.warn('No clips found, falling back to exercise mode');
          appState.currentClipIdx = 0;
          switchMode('exercise');
          setCurrentClip(appState.currentClipIdx);
          updateProgressUI();
        }
        return;
      } catch (fallbackErr: any) {
        // Both attempts failed
        logger.error('Failed to load clips (fallback also failed)', fallbackErr);
        throw fallbackErr;
      }
    }
    
    // Show error message to user
    logger.error('Failed to load clips', err);
    const mainContent = document.querySelector('main');
    if (mainContent) {
      mainContent.innerHTML = `
        <div class="error-message w-full max-w-md mx-auto p-4 bg-red-50 border border-red-300 rounded-lg text-red-800 my-8">
          <h2 class="font-bold mb-2">Failed to Load Content</h2>
          <p>Sorry, we couldn't load the audio clips. Please try refreshing the page.</p>
          <p class="text-sm mt-2">Technical details: ${err.message}</p>
        </div>
      `;
    }
  }
}

/**
 * Setup UI event handlers (buttons, mobile menu, etc.)
 */
export function setupUIEventHandlers(
  switchMode: (mode: string) => void,
  renderSettingsModalFn: () => void
): void {
  // Add click handler for logo/title button to return to home view
  const logoTitleBtn = document.getElementById('logo-title-btn');
  if (logoTitleBtn) {
    logoTitleBtn.addEventListener('click', () => {
      if (appState.mode !== 'home') {
        switchMode('home');
      }
    });
  }
  
  // Add click handler for settings button
  const settingsBtn = document.getElementById('settings-btn');
  if (settingsBtn) {
    settingsBtn.addEventListener('click', () => {
      renderSettingsModalFn();
    });
  }
  
  // Setup mobile menu handlers
  setupMobileMenuHandlers();
}

/**
 * Initialize debug controls if enabled
 */
function initDebugControls(): void {
  // Debug controls initialization
  // This is a placeholder for actual debug control setup
  logger.debug('Debug controls initialized');
}

/**
 * Report style token migration for debug mode
 */
function reportStyleTokenMigration(): void {
  if ((window as any)._styleTokenMigrationReported) {
    return;
  }
  (window as any)._styleTokenMigrationReported = true;
  
  const migrationData = {
    removedCount: 32,
    newTokens: [
      'btn-reset-focus',
      'logo-icon-container', 
      'progress-text',
      'container-full',
      'feedback-text',
      'page-backdrop',
      'app-title',
      'width-full'
    ],
    originalTokenCount: 38,
    finalTokenCount: 6,
    reductionPercentage: 84.2,
    methodology: 'Identified recurring utility patterns and converted to semantic CSS classes with design tokens'
  };
  
  const eventType = EVENT_TYPES['STYLE_TOKEN_MIGRATION_REPORT'];
  if (eventType) {
    logEvent(eventType, migrationData);
  }
}

/**
 * Initialize auth UI
 */
async function initializeAuth(): Promise<void> {
  function updateAuthUI() {
    const user = authService.getCurrentUser();
    const accountLabel = document.getElementById('account-label');
    const userEmail = document.getElementById('user-email');
    
    if (user && accountLabel && userEmail && user.email) {
      const username = user.email.split('@')[0];
      accountLabel.textContent = username && username.length > 12 ? username.substring(0, 12) + '...' : username || '';
      userEmail.textContent = user.email;
    } else if (accountLabel) {
      accountLabel.textContent = i18nService.getString('signIn') || 'Sign In';
    }
    
    // Update header actions to show/hide admin buttons based on auth state
    router.updateHeaderActions();
  }
  
  await authService.initialize();
  authService.initAuthUI(() => {
    updateAuthUI();
  }, showToast);
  
  // Check if user is in password recovery mode
  checkPasswordRecoveryMode();
}

/**
 * Check if user is in password recovery mode and handle it
 */
function checkPasswordRecoveryMode(): void {
  const urlParams = new URLSearchParams(window.location.search);
  const mode = urlParams.get('mode');
  
  // Check if user is in password recovery mode
  if (mode === 'reset') {
    logger.info('Password recovery mode detected');
    
    // Show password reset modal
    const resetModal = document.createElement('div');
    resetModal.id = 'password-reset-modal';
    resetModal.className = 'modal-overlay';
    resetModal.innerHTML = `
      <div class="modal-container modal-narrow">
        <div class="modal-header">
          <h2 class="modal-title">${i18nService.getString('resetPassword') || 'Reset Password'}</h2>
        </div>
        <div class="modal-body">
          <p class="text-body-secondary" style="margin-bottom: 1.5rem;">
            ${i18nService.getString('enterNewPassword') || 'Enter your new password below.'}
          </p>
          <form id="password-reset-form">
            <div class="form-group">
              <label for="new-password" class="form-label">${i18nService.getString('password') || 'Password'}</label>
              <input 
                type="password" 
                id="new-password" 
                class="form-input" 
                placeholder="••••••••" 
                required 
                minlength="6"
                autocomplete="new-password"
              />
            </div>
            <div class="form-group">
              <label for="confirm-new-password" class="form-label">${i18nService.getString('confirmPassword') || 'Confirm Password'}</label>
              <input 
                type="password" 
                id="confirm-new-password" 
                class="form-input" 
                placeholder="••••••••" 
                required 
                minlength="6"
                autocomplete="new-password"
              />
            </div>
            <button type="submit" class="btn-primary" style="width: 100%; margin-top: 1rem;">
              ${i18nService.getString('resetPassword') || 'Reset Password'}
            </button>
          </form>
        </div>
      </div>
    `;
    
    document.body.appendChild(resetModal);
    
    // Focus on first input
    setTimeout(() => {
      const newPasswordInput = document.getElementById('new-password') as HTMLInputElement | null;
      if (newPasswordInput) newPasswordInput.focus();
    }, 100);
    
    // Handle form submission
    const form = document.getElementById('password-reset-form');
    if (form) {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        
        const newPasswordInput = document.getElementById('new-password') as HTMLInputElement | null;
        const confirmPasswordInput = document.getElementById('confirm-new-password') as HTMLInputElement | null;
        
        const newPassword = newPasswordInput ? newPasswordInput.value : '';
        const confirmPassword = confirmPasswordInput ? confirmPasswordInput.value : '';
        
        if (newPassword !== confirmPassword) {
          showToast(i18nService.getString('passwordsDoNotMatch') || 'Passwords do not match.', 'error');
          return;
        }
        
        await authService.setNewPassword(newPassword, showToast);
        
        // Remove modal and clean up URL
        document.body.removeChild(resetModal);
        window.history.replaceState({}, document.title, window.location.pathname);
      });
    }
  }
}

/**
 * Initialize the application
 */
export async function initApp(
  renderHomeView: () => void,
  renderLibraryView: () => void,
  renderAdminView: () => void,
  switchMode: (mode: string) => void,
  setCurrentClip: (idx: number) => void,
  updateProgressUI: () => void,
  _callbacks: {
    resetClipState: () => void;
    renderClip: (idxOrClip: number | Clip) => void;
    savePreferences: () => void;
  }
): Promise<void> {
  logger.info('Initializing Audelio application');
  
  // Register router callbacks for view rendering
  router.registerCallbacks({
    removeTranscriptEventListeners: TranscriptModule.removeTranscriptEventListeners,
    renderHomeView,
    renderLibraryView,
    renderAdminView
  });
  
  // Clean up any transcript elements that might exist from a previous session
  TranscriptModule.removeTranscriptEventListeners();
  
  // Reset global flags
  appState.transcriptInjected = false;
  
  // Load saved state
  ProgressService.loadProgressMap();
  ProgressService.loadWeeklyActivity();
  ProgressService.recordTodayActivity();
  loadPreferences();
  ProgressService.loadTranscriptVisibility();
  
  // Initialize auth
  initializeAuth().catch(err => {
    logger.error('Failed to initialize auth', err);
  });
  
  // Log app open event
  const eventType = EVENT_TYPES['APP_OPEN'];
  if (eventType) {
    logEvent(eventType, {});
  }
  
  // Load UI strings and continue initialization
  loadUIStrings(i18nService.getCurrentLanguage(), () => {
    updateFooter();
    
    // Update header labels with translated text
    const settingsLabel = document.getElementById('settings-label');
    if (settingsLabel) {
      settingsLabel.textContent = i18nService.getString('settings') || 'Settings';
    }
    const accountLabel = document.getElementById('account-label');
    if (accountLabel && !authService.getCurrentUser()) {
      accountLabel.textContent = i18nService.getString('signIn') || 'Sign In';
    }
    
    // Setup UI event handlers
    setupUIEventHandlers(switchMode, renderSettingsModal);
    
    // Load clips
    loadClips(switchMode, setCurrentClip, updateProgressUI);
    
    // Check if onboarding is needed
    if (appFlags.enableOnboardingFlow && !localStorage.getItem('listen-up-onboarding-completed')) {
      showOnboardingModal();
    }
    
    // Initialize debug controls if enabled
    if (appState.debugEventsEnabled) {
      initDebugControls();
      const initEventType = EVENT_TYPES['APP_INIT'];
      if (initEventType) {
        logEvent(initEventType, { version: '0.1.0' });
      }
      reportStyleTokenMigration();
    }
  });
}
